import { Component, inject, input, OnInit, signal } from '@angular/core';
import { ILpCanvasComponent, ILpCanvasElementContext } from '@gametailors/leerplus';
import { generateUid } from '@gametailors/web-core/misc';
import { LpRidAssignmentsCanvasComponent, LpRidMechanicConfig } from '../../../../typings/schema';
import { RidStopAssignment } from '../../components/rid-stop-assignment/rid-stop-assignment';
import { LEERPLUS } from '../../rid-game.utils';
import { RidAssignmentActivityService } from '../../services/rid-assignment-activity.service';
import { RidHomeworkService } from '../../services/rid-homework.service';

@Component({
  selector: 'rid-assignments-canvas-component',
  imports: [RidStopAssignment],
  templateUrl: './rid-assignments-canvas-component.html',
  providers: [RidAssignmentActivityService],
})
export class RidAssignmentsCanvasComponent implements OnInit, ILpCanvasComponent<LpRidAssignmentsCanvasComponent> {
  protected readonly $lpApiService = inject(LEERPLUS.apiToken);
  private readonly $assignmentActivityService = inject(RidAssignmentActivityService);
  private readonly $ridHomeworkService = inject(RidHomeworkService);

  comp = input.required<LpRidAssignmentsCanvasComponent>();
  context = input.required<ILpCanvasElementContext>();

  /** The assignment whose content is being generated, so it isn't started twice. */
  protected readonly startingUid = signal<string | undefined>(undefined);
  /** The assignment that failed to start, until another one is started. */
  protected readonly failedUid = signal<string | undefined>(undefined);

  ngOnInit(): void {
    this.context().requirePointerEvents();
  }

  isFinished(config: LpRidMechanicConfig): boolean {
    const linkedActivityUid = this.findLinkedActivityUid(config);
    if (!linkedActivityUid) return false;

    return this.$lpApiService.state().activityProgress.some((p) => p.uid === linkedActivityUid && !!p.finished_timestamp);
  }

  async startAssignment(config: LpRidMechanicConfig) {
    if (this.isFinished(config) || this.startingUid()) return;

    const currentActivity = this.$lpApiService.currentMission()?.activity;
    if (!currentActivity) throw new Error('No current activity to return to after finishing the assignment');

    this.startingUid.set(config.uid);
    this.failedUid.set(undefined);
    try {
      const activity = await this.$assignmentActivityService.createActivity(config, currentActivity);
      this.linkActivity(config, activity.uid);
      await this.$lpApiService.switchActivity(activity, { dataType: 'FadeTransition', uid: generateUid() });
    } catch (error) {
      console.error(`Failed to start assignment ${config.uid} (${config.dataType})`, error);
      this.failedUid.set(config.uid);
    } finally {
      this.startingUid.set(undefined);
    }
  }

  private findLinkedActivityUid(config: LpRidMechanicConfig): string | undefined {
    const stopUid = this.$ridHomeworkService.currentStop()?.uid;
    const link = this.$lpApiService.state().homeworkLinks.stops.find((l) => l.locationStopUid === stopUid);
    return link?.assignmentActivityUids[config.uid];
  }

  private linkActivity(config: LpRidMechanicConfig, activityUid: string) {
    const stopUid = this.$ridHomeworkService.currentStop()?.uid;
    this.$lpApiService.state.update((save) => ({
      ...save,
      homeworkLinks: {
        ...save.homeworkLinks,
        stops: save.homeworkLinks.stops.map((l) =>
          l.locationStopUid !== stopUid ? l : { ...l, assignmentActivityUids: { ...l.assignmentActivityUids, [config.uid]: activityUid } },
        ),
      },
    }));
  }
}
